import { useMemo } from 'react';

import useExplorerColor from '@/features/InsightExplorer/hooks/useExplorerColor';
import {
  type InsightExplorerByType,
  type InsightExplorerFieldType,
  type InsightExplorerMetricType,
  InsightExplorerViewType,
} from '@/features/InsightExplorer/types/constants';
import type {
  InsightExplorerDistributionResponse,
  InsightExplorerStatistics,
} from '@/features/InsightExplorer/types/types';

import type { HierarchyNodeData } from '../../InsightExplorerSidebar/HierarchyNavigator/HierarchyNavigator';
import type { SubtopicItem } from '../types';

type Props = {
  view: InsightExplorerViewType;
  childrenData: InsightExplorerDistributionResponse;
  findNode: (id: string) => HierarchyNodeData | null;
  metricType: InsightExplorerMetricType;
  statistics: InsightExplorerStatistics;
  by: InsightExplorerByType;
  field: InsightExplorerFieldType;
  exceptSelf?: boolean;
};

export const useSubtopicSection = (props: Props) => {
  const { childrenData, field } = props;
  const getLabelColor = useExplorerColor(field);

  // Subtopics 데이터 변환
  const subtopicsData: SubtopicItem[] = useMemo(() => {
    if (!childrenData) return [];
    if (props.view === InsightExplorerViewType.SENTIMENTS) {
      return childrenData.map((child, index) => ({
        id: child.alias,
        name: props.findNode(child.alias)?.name || child.alias,
        count: child.negative_volume,
        color: getLabelColor(child.alias),
        isActive: index === 0, // 첫 번째 항목을 active로 설정
        share: props.exceptSelf
          ? 0
          : props.statistics.negativeTotalVolume > 0
            ? (child.negative_volume / props.statistics.negativeTotalVolume) *
              100
            : 0,
      }));
    }

    return childrenData.map((child, index) => ({
      id: child.alias,
      name: props.findNode(child.alias)?.name || child.alias,
      count: child.volume,
      color: getLabelColor(child.alias),
      isActive: index === 0, // 첫 번째 항목을 active로 설정
      share: props.exceptSelf
        ? 0
        : props.statistics.totalVolume > 0
          ? (child.volume / props.statistics.totalVolume) * 100
          : 0,
    }));
  }, [childrenData, getLabelColor, props]);

  return {
    subtopicsData,
  };
};
